/**
 * Pure fit-state ladder for a toolbar's room-based collapse. Three states,
 * widest to narrowest:
 *
 *   'full'      — every control inline, labels visible
 *   'icons'     — the same controls inline but icon-only
 *   'collapsed' — a single chevron opening a menu holding all of them
 *
 * Two toolbars walk it: the list header (Columns / Group by) and
 * the track-list header (Play all / Export).
 * `useToolbarFit` owns the measuring loop and hands this resolver one
 * measurement of the current layout; it returns the next state:
 *
 * - **Demotion** is one step per measure pass: when the current layout
 *   overflows its box we step down exactly one state (full → icons →
 *   collapsed) and let the re-render + re-measure loop decide whether to step
 *   down again. Never skips a state, so each state's real laid-out width gets
 *   cached on the way down.
 * - **Promotion** is also one step, and only when the next-wider state's
 *   *cached* laid-out width is known to fit with at least `hysteresisPx` to
 *   spare. Unknown cache (never rendered, or invalidated because a label
 *   changed) means no promotion — the caller re-learns widths by resetting to
 *   'full' and letting demotion settle.
 *
 * No flapping at the boundary: promotion needs `clientWidth >=
 * cachedWidth + hysteresisPx` while demotion needs `scrollWidth >
 * clientWidth + OVERFLOW_TOLERANCE_PX`, so there is a sticky band of at least
 * `hysteresisPx` where whichever state we're in stays put.
 */

import type { SyncPillForm } from '@/utils/syncStatus';

export type ToolbarFitState = 'full' | 'icons' | 'collapsed';

/**
 * Laid-out content widths (scrollWidth) each expanded state needed the last
 * time it was actually rendered. 'collapsed' is never a promotion target via
 * cache, so it isn't tracked.
 */
export type ToolbarStateWidths = Partial<
  Record<Exclude<ToolbarFitState, 'collapsed'>, number>
>;

/** Same buffer #117 used (COLLAPSE_HYSTERESIS_PX) to avoid boundary flapping. */
export const TOOLBAR_FIT_HYSTERESIS_PX = 8;

/** scrollWidth can exceed clientWidth by a rounding pixel without real overflow. */
const OVERFLOW_TOLERANCE_PX = 1;

const NARROWER_STATE: Record<ToolbarFitState, ToolbarFitState | null> = {
  full: 'icons',
  icons: 'collapsed',
  collapsed: null,
};

const WIDER_STATE: Record<
  ToolbarFitState,
  Exclude<ToolbarFitState, 'collapsed'> | null
> = {
  full: null,
  icons: 'full',
  collapsed: 'icons',
};

export interface ToolbarFitInput {
  /** The state whose layout was just measured (i.e. currently rendered). */
  currentState: ToolbarFitState;
  /** scrollWidth of the measured toolbar row — the width its content needs. */
  scrollWidth: number;
  /** clientWidth of the measured toolbar row — the room actually available. */
  clientWidth: number;
  /** Cached laid-out widths of the wider states (see ToolbarStateWidths). */
  cachedStateWidths: ToolbarStateWidths;
  /** Extra room required before promoting; defaults to TOOLBAR_FIT_HYSTERESIS_PX. */
  hysteresisPx?: number;
}

/**
 * Decide the next toolbar fit state from a single measurement of the current
 * one. Pure — the caller owns the width cache (including recording the
 * current state's scrollWidth into it and invalidating it when label sources
 * change).
 */
export function resolveNextToolbarFitState(input: ToolbarFitInput): ToolbarFitState {
  const {
    currentState,
    scrollWidth,
    clientWidth,
    cachedStateWidths,
    hysteresisPx = TOOLBAR_FIT_HYSTERESIS_PX,
  } = input;

  // Overflowing: demote exactly one step (or stay if already at the floor).
  if (scrollWidth > clientWidth + OVERFLOW_TOLERANCE_PX) {
    return NARROWER_STATE[currentState] ?? currentState;
  }

  // Fits: promote one step only when we know the wider layout fits with room
  // to spare. An unset cache entry blocks promotion by design.
  const promotionCandidate = WIDER_STATE[currentState];
  if (promotionCandidate !== null) {
    const cachedWiderWidth = cachedStateWidths[promotionCandidate];
    if (
      cachedWiderWidth !== undefined &&
      clientWidth >= cachedWiderWidth + hysteresisPx
    ) {
      return promotionCandidate;
    }
  }

  return currentState;
}

/**
 * Where the search toolbar's status cluster (the Sync pill and the live-polling
 * dot) goes, and which form the pill takes. The pill's form follows the fit
 * ladder's state and is never measured on its own: "Sync 7 · 5" while the
 * toolbar shows words, the icon once it shows icons.
 *
 * Only the fit ladder measures. On develop before SAU-576 the pill measured
 * the room left on the row too, and at some widths the two overruled each
 * other forever and froze the tab.
 *
 * - On the Players list (`hasSyncPill`) the cluster sits inside the measured
 *   row while the toolbar is full or icons, on desktop too, so the ladder
 *   measures the pill like any other control. Beside the pager it would make
 *   the pager group wider in full than in icons, so the row would have less
 *   room in full than in icons, and the ladder would flip between the two.
 * - On every other list, which has only the live dot, the cluster sits by the
 *   pager at md+, and trails the toolbar buttons below md.
 * - When the toolbar collapses, the cluster moves to the right group. Below md
 *   that group is its own full-width row with the pager, where the pill
 *   competes with nothing and shows its counts. At md+ it sits beside the
 *   pager, which narrows the row, so it stays an icon there. Narrowing the row
 *   only makes promoting harder, so that cannot flip.
 */
export interface SearchToolbarStatusLayout {
  inLeftGroup: boolean;
  form: SyncPillForm;
}

export function searchToolbarStatusLayout(
  isDesktop: boolean,
  fitState: ToolbarFitState,
  hasSyncPill: boolean,
): SearchToolbarStatusLayout {
  const inLeftGroup = fitState !== 'collapsed' && (hasSyncPill || !isDesktop);
  if (fitState === 'full') return { inLeftGroup, form: 'full' };
  if (fitState === 'icons') return { inLeftGroup, form: 'icon' };
  return { inLeftGroup, form: isDesktop ? 'icon' : 'full' };
}
